import React, { useState } from 'react';
import { Box, Typography, Fade, Chip } from '@mui/material';
import { Play, Clock, Shield, CheckCircle2 } from 'lucide-react';
import { styled } from '@mui/material/styles';

const VideoCard = styled(Box)(({ theme, active }) => ({
  background: active ? 'rgba(124,58,237,0.08)' : '#141926',
  border: active ? '1px solid rgba(124,58,237,0.3)' : '1px solid rgba(255,255,255,0.06)',
  borderRadius: 12,
  overflow: 'hidden',
  cursor: 'pointer',
  transition: 'border-color 0.2s',
  '&:hover': {
    borderColor: active ? 'rgba(124,58,237,0.4)' : 'rgba(255,255,255,0.12)',
  },
}));

const videos = [
  {
    id: 'bm-verification-button',
    src: '/aulas/bm-verification-button.mp4',
    thumbnail: '/aulas/bm-verification-button.jpg',
    title: 'Como habilitar o botão de verificação da BM',
    description: 'Passo a passo prático para liberar o botão de verificação no Business Manager. Mostra exatamente onde clicar, o que ajustar e como destravar o fluxo de verificação caso o botão não esteja aparecendo na sua BM.',
    icon: <CheckCircle2 size={18} strokeWidth={1.75} />,
    tag: 'BM 2026',
    tagColor: '#10B981',
  },
  {
    id: 'aAJaeVKxCwg',
    title: 'Novo método de verificação de BM em 2026',
    description: 'Método atualizado de 2026 para verificar Business Manager e portfólio empresarial usando o FTM Legacy: CNPJ + IA + domínio próprio + SSL automático. Passo a passo completo do processo.',
    icon: <Shield size={18} strokeWidth={1.75} />,
    tag: 'BM 2026',
    tagColor: '#7C3AED',
  },
];

export default function VideoAulas() {
  const [selected, setSelected] = useState(videos[0]);

  return (
    <Fade in timeout={400}>
      <Box sx={{ maxWidth: 960, mx: 'auto', p: { xs: 1, sm: 0 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Play size={28} strokeWidth={1.75} color="#7C3AED" />
          <Typography variant="h4" sx={{ color: '#fff' }}>Vídeo Aulas</Typography>
        </Box>
        <Typography sx={{ color: '#9CA3AF', mb: 4 }}>
          Aprenda a usar todas as funcionalidades do Painel FTM.
        </Typography>

        <Box sx={{
          position: 'relative',
          width: '100%',
          paddingTop: '56.25%',
          borderRadius: '12px',
          overflow: 'hidden',
          border: '1px solid rgba(255,255,255,0.06)',
          mb: 3,
          background: '#000',
        }}>
          {selected.src ? (
            <video
              key={selected.id}
              src={selected.src}
              poster={selected.thumbnail}
              controls
              controlsList="nodownload"
              preload="metadata"
              style={{
                position: 'absolute',
                top: 0, left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                background: '#000',
              }}
            />
          ) : (
            <iframe
              src={`https://www.youtube.com/embed/${selected.id}?rel=0&modestbranding=1`}
              title={selected.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{
                position: 'absolute',
                top: 0, left: 0,
                width: '100%',
                height: '100%',
                border: 'none',
              }}
            />
          )}
        </Box>

        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <Typography variant="h5" sx={{ color: '#fff' }}>
              {selected.title}
            </Typography>
            <Chip
              label={selected.tag}
              size="small"
              sx={{ background: `${selected.tagColor}20`, color: selected.tagColor, fontWeight: 600, fontSize: '0.7rem' }}
            />
          </Box>
          <Typography sx={{ color: '#9CA3AF', fontSize: '0.95rem' }}>
            {selected.description}
          </Typography>
        </Box>

        <Typography sx={{ color: '#6B7280', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, mb: 2 }}>
          Todas as aulas
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {videos.map((video, index) => (
            <VideoCard
              key={video.id}
              active={selected.id === video.id}
              onClick={() => setSelected(video)}
            >
              <Box sx={{ display: 'flex', gap: 2, p: 2, alignItems: 'center' }}>
                <Box sx={{
                  position: 'relative',
                  width: 160, minWidth: 160, height: 90,
                  borderRadius: 2, overflow: 'hidden',
                  background: '#000',
                  display: { xs: 'none', sm: 'block' },
                }}>
                  <img
                    src={video.thumbnail || `https://img.youtube.com/vi/${video.id}/mqdefault.jpg`}
                    alt={video.title}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                  {selected.id === video.id && (
                    <Box sx={{
                      position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                      background: 'rgba(124,58,237,0.3)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Play size={28} strokeWidth={2} color="#fff" fill="#fff" />
                    </Box>
                  )}
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Chip
                      icon={video.icon}
                      label={video.tag}
                      size="small"
                      sx={{
                        background: `${video.tagColor}15`,
                        color: video.tagColor,
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        '& .MuiChip-icon': { color: 'inherit' },
                      }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Clock size={14} strokeWidth={1.75} color="#6B7280" />
                      <Typography sx={{ color: '#6B7280', fontSize: '0.75rem' }}>
                        Aula {index + 1}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography sx={{
                    color: selected.id === video.id ? '#fff' : '#D1D5DB',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {video.title}
                  </Typography>
                  <Typography sx={{
                    color: '#9CA3AF',
                    fontSize: '0.8rem',
                    mt: 0.5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: { xs: 'none', md: 'block' },
                  }}>
                    {video.description}
                  </Typography>
                </Box>
              </Box>
            </VideoCard>
          ))}
        </Box>
      </Box>
    </Fade>
  );
}
